import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../Store/userSlice';
import { TOKEN_KEYS } from '../components/common/Util/Util';
import { preserveAuthTokens, restoreAuthTokens, ensureAuthTokens } from '../components/common/Util/SessionTokens';

const WARNING_TIMEOUT = 10 * 60 * 1000;
const LOGOUT_AFTER_WARNING = 5 * 60 * 1000;

let logoutInProgress = false;
let timerHasExpired = false;

const TEMP_LOGOUT_TOKEN = 'temp_logout_token';
const SESSION_TIMEOUT_IN_PROGRESS = 'sessionTimeoutInProgress';
const SESSION_CHANNEL = new BroadcastChannel('session_management');
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

const useIdleTimer = () => {
  const [isIdle, setIsIdle] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [autoSaveTriggered, setAutoSaveTriggered] = useState(false);
  const [tokenCaptured, setTokenCaptured] = useState(false);

  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user?.user);
  const authState = useSelector((state) => state.auth);
  const combinedUser = useMemo(
    () => ({
      user: userState,
      jwtToken: authState?.token || userState?.jwtToken,
      token: authState?.token,
      webToken: authState?.webToken,
    }),
    [authState?.token, authState?.webToken, userState]
  );
  const storageToken = localStorage.getItem('token') || localStorage.getItem(TOKEN_KEYS.JWT);
  const isAuthenticated = Boolean(userState?.jwtToken || storageToken);

  const logoutTriggeredRef = useRef(false);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningTimeoutRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const clearAllStorage = useCallback(() => {
    dispatch(setUser({}));

    localStorage.removeItem(TOKEN_KEYS.USER_INFO);
    localStorage.removeItem(TOKEN_KEYS.JWT);
    localStorage.removeItem(TOKEN_KEYS.BUSINESS_TYPE);
    localStorage.removeItem(TOKEN_KEYS.ENTITY_TYPE);
    localStorage.removeItem(TOKEN_KEYS.SESSION_TIMEOUT);
    localStorage.removeItem(SESSION_TIMEOUT_IN_PROGRESS);
    localStorage.removeItem('confirmedSessionTimeout');
    localStorage.removeItem('manualLogoutConfirmed');
    localStorage.removeItem(TEMP_LOGOUT_TOKEN);
    localStorage.removeItem('token');
    localStorage.removeItem('webToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('persist:root');

    sessionStorage.clear();

    if (window._preservedAuthTokens) {
      delete window._preservedAuthTokens;
    }
    if (window._tokenCaptureTime) {
      delete window._tokenCaptureTime;
    }
  }, [dispatch]);

  const performLogout = useCallback(() => {
    if (logoutInProgress || logoutTriggeredRef.current) {
      return;
    }

    try {
      logoutInProgress = true;
      logoutTriggeredRef.current = true;
      setIsIdle(true);
      timerHasExpired = true;

      SESSION_CHANNEL.postMessage({ type: 'LOGOUT_INITIATED' });

      clearAllTimers();
      clearAllStorage();
      window.location.replace('/');
    } catch (error) {
      console.error('Error during session timeout logout:', error);
      clearAllStorage();
      window.location.replace('/');
    }
  }, [clearAllTimers, clearAllStorage]);

  const startWarningTimer = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }
    clearAllTimers();

    lastActivityRef.current = Date.now();

    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setAutoSaveTriggered(false);
      setTokenCaptured(true);

      SESSION_CHANNEL.postMessage({ type: 'SHOW_WARNING' });

      if (combinedUser?.jwtToken || combinedUser?.token) {
        window._tokenCaptureTime = Date.now();
        preserveAuthTokens();
      }

      logoutTimerRef.current = setTimeout(() => {
        if (!logoutInProgress && !logoutTriggeredRef.current) {
          performLogout();
        }
      }, LOGOUT_AFTER_WARNING);
    }, WARNING_TIMEOUT);
  }, [isAuthenticated, combinedUser, clearAllTimers, performLogout]);

  const handleUserActivity = useCallback(() => {
    if (timerHasExpired || logoutInProgress || logoutTriggeredRef.current) {
      return;
    }

    if (showWarning) {
      return;
    }
    lastActivityRef.current = Date.now();

    SESSION_CHANNEL.postMessage({
      type: 'USER_ACTIVITY',
      timestamp: lastActivityRef.current,
    });

    if (localStorage.getItem(TOKEN_KEYS.SESSION_TIMEOUT)) {
      localStorage.removeItem(TOKEN_KEYS.SESSION_TIMEOUT);
    }

    startWarningTimer();
  }, [showWarning, startWarningTimer]);

  const resetTimerManually = useCallback(() => {
    if (timerHasExpired || logoutInProgress || logoutTriggeredRef.current) {
      return;
    }

    SESSION_CHANNEL.postMessage({ type: 'CONTINUE_SESSION' });

    setShowWarning(false);
    setAutoSaveTriggered(false);
    setTokenCaptured(false);
    timerHasExpired = false;

    localStorage.removeItem('sessionTimeoutInProgress');
    localStorage.removeItem(TOKEN_KEYS.SESSION_TIMEOUT);
    localStorage.removeItem('confirmedSessionTimeout');

    restoreAuthTokens(combinedUser);

    lastActivityRef.current = Date.now();

    startWarningTimer();
  }, [combinedUser, startWarningTimer]);

  const triggerAutoSave = useCallback(() => {
    if (!autoSaveTriggered) {
      setAutoSaveTriggered(true);

      if (combinedUser?.jwtToken || combinedUser?.token) {
        ensureAuthTokens(combinedUser);
      }

      localStorage.setItem(SESSION_TIMEOUT_IN_PROGRESS, 'true');
      window.dispatchEvent(new CustomEvent('sessionTimeout'));

      return true;
    }
    return false;
  }, [autoSaveTriggered, combinedUser]);

  useEffect(() => {
    const handleChannelMessage = (event) => {
      const { type, timestamp } = event.data;

      switch (type) {
        case 'USER_ACTIVITY':
          if (!showWarning && timestamp > lastActivityRef.current) {
            lastActivityRef.current = timestamp;
            startWarningTimer();
          }
          break;

        case 'SHOW_WARNING':
          if (!showWarning && isAuthenticated) {
            setShowWarning(true);
            setAutoSaveTriggered(false);
            setTokenCaptured(true);
          }
          break;

        case 'CONTINUE_SESSION':
          setShowWarning(false);
          setAutoSaveTriggered(false);
          setTokenCaptured(false);
          startWarningTimer();
          break;

        case 'LOGOUT_INITIATED':
          clearAllTimers();
          performLogout();
          break;
        default:
          break;
      }
    };

    SESSION_CHANNEL.addEventListener('message', handleChannelMessage);

    if (isAuthenticated) {
      startWarningTimer();
    }

    return () => {
      SESSION_CHANNEL.removeEventListener('message', handleChannelMessage);
      clearAllTimers();
    };
  }, [clearAllTimers, performLogout, showWarning, startWarningTimer, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      setAutoSaveTriggered(false);
      setTokenCaptured(false);
      timerHasExpired = false;
      logoutInProgress = false;
    }
  }, [isAuthenticated, clearAllTimers]);

  useEffect(() => {
    const events = ACTIVITY_EVENTS;

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    if (isAuthenticated) {
      startWarningTimer();
    }

    const handleImmediateTimeout = () => {
      clearAllTimers();
      timerHasExpired = true;

      localStorage.removeItem(TEMP_LOGOUT_TOKEN);
      localStorage.removeItem(SESSION_TIMEOUT_IN_PROGRESS);

      performLogout();
    };

    window.addEventListener('immediateTimeout', handleImmediateTimeout);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });

      clearAllTimers();
      window.removeEventListener('immediateTimeout', handleImmediateTimeout);

      if (!timerHasExpired && !logoutInProgress) {
        restoreAuthTokens(combinedUser);
      }
    };
  }, [isAuthenticated, handleUserActivity, combinedUser, performLogout, clearAllTimers, startWarningTimer]);

  return { isIdle, showWarning, resetTimerManually, triggerAutoSave, tokenCaptured };
};

export default useIdleTimer;

